import { useState, useEffect, type FormEvent } from 'react';
import { useAccountProfile, useUpdateAccountProfile, useChangePassword } from '@/hooks/useAccount';
import type { UpdateProfileInput } from '@/types/account';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const ROLE_LABELS: Record<string, string> = {
  OWNER: 'Owner', ADMIN: 'Admin', MEMBER: 'Mitglied', VIEWER: 'Betrachter',
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Nie';
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(new Date(dateStr));
}

function SkeletonCard({ rows }: { rows: number }) {
  return (
    <Card>
      <CardHeader><div className="h-6 w-32 animate-pulse rounded bg-muted" /></CardHeader>
      <CardContent className="space-y-4">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-4 w-24 animate-pulse rounded bg-muted" />
            <div className="h-10 animate-pulse rounded bg-muted" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function AccountPage() {
  const { data, isLoading, isError, error, refetch } = useAccountProfile();
  const profileMutation = useUpdateAccountProfile();
  const passwordMutation = useChangePassword();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (data) {
      setFirstName(data.first_name ?? '');
      setLastName(data.last_name ?? '');
      setEmail(data.email ?? '');
    }
  }, [data]);

  useEffect(() => {
    if (!profileSuccess) return;
    const t = setTimeout(() => setProfileSuccess(''), 3000);
    return () => clearTimeout(t);
  }, [profileSuccess]);

  useEffect(() => {
    if (!passwordSuccess) return;
    const t = setTimeout(() => setPasswordSuccess(''), 3000);
    return () => clearTimeout(t);
  }, [passwordSuccess]);

  function handleProfileSubmit(e: FormEvent) {
    e.preventDefault();
    if (!data) return;

    const payload: UpdateProfileInput = {};
    if (firstName !== (data.first_name ?? '')) payload.first_name = firstName;
    if (lastName !== (data.last_name ?? '')) payload.last_name = lastName;
    if (email !== data.email) payload.email = email;

    if (Object.keys(payload).length === 0) {
      setProfileSuccess('Keine Änderungen vorhanden.');
      return;
    }

    profileMutation.mutate(payload, {
      onSuccess: () => setProfileSuccess('Profil erfolgreich gespeichert.'),
    });
  }

  function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault();
    setPasswordError('');

    if (newPassword.length < 8) {
      setPasswordError('Neues Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwörter stimmen nicht überein.');
      return;
    }

    passwordMutation.mutate(
      { current_password: currentPassword, new_password: newPassword },
      {
        onSuccess: () => {
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setPasswordSuccess('Passwort erfolgreich geändert.');
          setPasswordError('');
        },
        onError: (err) => {
          setPasswordError(
            err instanceof Error ? err.message : 'Passwort ändern fehlgeschlagen.'
          );
        },
      }
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold">Konto</h1>
      <p className="mt-2 text-muted-foreground">Verwalten Sie Ihr Benutzerprofil.</p>

      <div className="mt-6">
        {isLoading && (
          <div className="space-y-6">
            <SkeletonCard rows={3} />
            <SkeletonCard rows={3} />
          </div>
        )}

        {isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
            <p className="text-sm text-red-700">
              Profil konnte nicht geladen werden:{' '}
              {error instanceof Error ? error.message : 'Unbekannter Fehler'}
            </p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
              Erneut versuchen
            </Button>
          </div>
        )}

        {data && (
          <div className="space-y-6">
            <form onSubmit={handleProfileSubmit}>
              <Card>
                <CardHeader><CardTitle className="text-lg">Profil</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">Vorname *</Label>
                      <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} required disabled={profileMutation.isPending} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Nachname *</Label>
                      <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} required disabled={profileMutation.isPending} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail *</Label>
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={profileMutation.isPending} />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Rolle: {ROLE_LABELS[data.role] ?? data.role} · Letzte Anmeldung: {formatDate(data.last_login_at)}
                  </p>
                  <div className="flex items-center justify-end gap-4">
                    {profileMutation.isError && (
                      <p className="text-sm text-destructive">Speichern fehlgeschlagen.</p>
                    )}
                    {profileSuccess && <p className="text-sm text-green-600">{profileSuccess}</p>}
                    <Button type="submit" disabled={profileMutation.isPending}>
                      {profileMutation.isPending ? 'Speichern...' : 'Profil speichern'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </form>

            <form onSubmit={handlePasswordSubmit}>
              <Card>
                <CardHeader><CardTitle className="text-lg">Passwort ändern</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Aktuelles Passwort *</Label>
                    <Input id="currentPassword" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required disabled={passwordMutation.isPending} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">Neues Passwort *</Label>
                    <Input id="newPassword" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} disabled={passwordMutation.isPending} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Neues Passwort bestätigen *</Label>
                    <Input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required disabled={passwordMutation.isPending} />
                  </div>
                  <div className="flex items-center justify-end gap-4">
                    {passwordError && <p className="text-sm text-destructive">{passwordError}</p>}
                    {passwordSuccess && <p className="text-sm text-green-600">{passwordSuccess}</p>}
                    <Button type="submit" disabled={passwordMutation.isPending}>
                      {passwordMutation.isPending ? 'Ändern...' : 'Passwort ändern'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
